/**
 * Finance / caisse i18n — FR default + AR-RTL. Registered once in the module's onRegister(). Keys
 * are namespaced under `finance.*` and `nav.finance.*`. Layout mirrors automatically under
 * dir="rtl" via CSS logical properties; mono numerics stay LTR with .nv-ltr-island.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav
  "nav.section.finance": "Finance",
  "nav.finance.caisse": "Caisse",
  "nav.finance.facturation": "Facturation",

  // shared
  "finance.title": "Finance / Caisse",
  "finance.currency": "DZD",
  "finance.action.refresh": "Actualiser",
  "finance.field.required": "Champ requis.",
  "finance.field.amountInvalid": "Montant invalide.",
  "finance.amount.aria": "Montant en dinars",

  // caisse
  "finance.caisse.title": "Caisse",
  "finance.caisse.subtitle": "Ouverture, encaissement et clôture de la session de caisse.",
  "finance.caisse.tab.session": "Session",
  "finance.caisse.tab.paiement": "Encaissement",
  "finance.caisse.none.title": "Aucune session ouverte",
  "finance.caisse.none.desc": "Ouvrez une session de caisse pour commencer à encaisser.",
  "finance.caisse.open.cta": "Ouvrir la caisse",
  "finance.caisse.open.title": "Ouvrir une session de caisse",
  "finance.caisse.open.caisseId": "Identifiant de caisse",
  "finance.caisse.open.caisseId.hint": "Le poste de caisse (ex. CAISSE-01).",
  "finance.caisse.open.fond": "Fond de caisse",
  "finance.caisse.open.fond.hint": "Espèces présentes dans le tiroir à l'ouverture.",
  "finance.caisse.open.submit": "Ouvrir la session",
  "finance.caisse.open.success": "Session de caisse ouverte.",
  "finance.caisse.openState": "Ouverte",
  "finance.caisse.closedState": "Clôturée",
  "finance.caisse.openedAt": "Ouverte le",
  "finance.caisse.caisseId": "Caisse",
  "finance.caisse.fond": "Fond de caisse",
  "finance.caisse.stats.title": "Encaissements de la session",
  "finance.caisse.stats.total": "Total encaissé",
  "finance.caisse.stats.rembourse": "Total remboursé",
  "finance.caisse.stats.theorique": "Espèces attendues",
  "finance.caisse.close.cta": "Clôturer la caisse",
  "finance.caisse.close.title": "Clôturer la session",
  "finance.caisse.close.warn": "La clôture est définitive : elle arrête les encaissements et génère le rapport Z.",
  "finance.caisse.close.comptees": "Espèces comptées",
  "finance.caisse.close.comptees.hint": "Montant réellement compté dans le tiroir.",
  "finance.caisse.close.submit": "Clôturer et générer le Z",
  "finance.caisse.close.success": "Session clôturée. Rapport Z généré.",
  "finance.caisse.z.title": "Rapport Z",
  "finance.caisse.z.ecart": "Écart de caisse",
  "finance.caisse.z.ecart.none": "Caisse juste",
  "finance.caisse.z.ecart.surplus": "Excédent",
  "finance.caisse.z.ecart.short": "Manquant",

  // paiement
  "finance.paiement.title": "Encaisser un paiement",
  "finance.paiement.needCaisse": "Une session de caisse doit être ouverte pour encaisser.",
  "finance.paiement.factureId": "Facture",
  "finance.paiement.factureId.hint": "Identifiant de la facture à régler.",
  "finance.paiement.montant": "Montant à régler",
  "finance.paiement.montant.hint": "Montant imputé à la facture.",
  "finance.paiement.tendu": "Montant remis",
  "finance.paiement.tendu.hint": "Somme remise par le patient.",
  "finance.paiement.mode": "Mode de règlement",
  "finance.paiement.mode.especes": "Espèces",
  "finance.paiement.mode.carte": "Carte",
  "finance.paiement.mode.cheque": "Chèque",
  "finance.paiement.mode.autre": "Autre",
  "finance.paiement.rendu": "Monnaie à rendre",
  "finance.paiement.tenduTooLow": "Le montant remis est inférieur au montant à régler.",
  "finance.paiement.submit": "Encaisser",
  "finance.paiement.success": "Paiement encaissé.",
  "finance.paiement.last": "Dernier encaissement",

  // facture
  "finance.facture.title": "Émettre une facture",
  "finance.facture.subtitle": "Composez les lignes, appliquez une remise, émettez.",
  "finance.facture.demandeId": "Demande d'analyse",
  "finance.facture.demandeId.hint": "Optionnel — rattache la facture à une demande.",
  "finance.facture.lignes": "Lignes",
  "finance.facture.ligne.libelle": "Libellé",
  "finance.facture.ligne.prix": "Prix",
  "finance.facture.ligne.add": "Ajouter une ligne",
  "finance.facture.ligne.remove": "Retirer la ligne",
  "finance.facture.ligne.none": "Ajoutez au moins une ligne.",
  "finance.facture.totalBrut": "Total brut",
  "finance.facture.remise": "Remise",
  "finance.facture.remise.type": "Type de remise",
  "finance.facture.remise.none": "Aucune remise",
  "finance.facture.remise.pourcentage": "Pourcentage",
  "finance.facture.remise.montant": "Montant fixe",
  "finance.facture.remise.valeurPct": "Pourcentage (%)",
  "finance.facture.remise.valeurMontant": "Montant de la remise",
  "finance.facture.remise.motif": "Motif de la remise",
  "finance.facture.remise.motif.hint": "Justification (médico-légal).",
  "finance.facture.remise.pctInvalid": "Pourcentage entre 0 et 100.",
  "finance.facture.remise.tooBig": "La remise dépasse le total brut.",
  "finance.facture.totalNet": "Net à payer",
  "finance.facture.submit": "Émettre la facture",
  "finance.facture.success": "Facture émise.",
  "finance.facture.issued.title": "Facture émise",
  "finance.facture.numero": "N° facture",
  "finance.facture.payNow": "Encaisser cette facture",

  // ── finance (06) — cash-desk extensions ─────────────────────────────────────
  // nav
  "nav.finance.coffre": "Coffre & clôture",
  "nav.finance.fidelite": "Fidélité",

  // shared (06)
  "finance.06.sessionId": "Identifiant de session",
  "finance.06.sessionId.hint": "Identifiant numérique de la session de caisse.",
  "finance.06.loadSession": "Charger la session",
  "finance.06.motif": "Motif",
  "finance.06.reference": "Référence",
  "finance.06.optional": "Optionnel",
  "finance.06.actions": "Actions",

  // coffre / clôture page
  "finance.coffre.title": "Coffre, clôture Z & mouvements",
  "finance.coffre.subtitle": "Clôture Z gelée, réouverture auditée, journal des mouvements et corrections de caisse.",
  "finance.coffre.none.title": "Aucune session ouverte",
  "finance.coffre.none.desc": "Ouvrez une session depuis la caisse, ou saisissez un identifiant de session ci-dessous.",
  "finance.coffre.manualLoad": "Charger une session par identifiant",
  "finance.coffre.tab.xreport": "Rapport X",
  "finance.coffre.tab.mouvements": "Mouvements",
  "finance.coffre.tab.corrections": "Corrections",
  "finance.coffre.tab.fermeture": "Clôture Z",

  // X-report
  "finance.x.title": "Rapport X (instantané)",
  "finance.x.subtitle": "Photographie de la caisse sans gel — recompté à chaque lecture.",
  "finance.x.etat": "État",
  "finance.x.etat.ouverte": "Ouverte",
  "finance.x.etat.fermee": "Fermée",
  "finance.x.fondOuverture": "Fond d'ouverture",
  "finance.x.totalEncaisse": "Total encaissé",
  "finance.x.totalRembourse": "Total remboursé",
  "finance.x.totalTheorique": "Total théorique",
  "finance.x.mouvementsCount": "Nombre de mouvements",
  "finance.x.frozen": "Z gelé",
  "finance.x.frozen.yes": "Oui",
  "finance.x.frozen.no": "Non",
  "finance.x.chart.title": "Composition du tiroir théorique",
  "finance.x.chart.fond": "Fond",
  "finance.x.chart.encaisse": "Encaissé",
  "finance.x.chart.rembourse": "Remboursé",
  "finance.x.chart.mouvements": "Mouvements nets",

  // Z-freeze close
  "finance.z.title": "Clôture Z gelée",
  "finance.z.warn": "La clôture gèle le Z (théorique, réel et écart). Toute correction ultérieure passe par le drawer ouvert, jamais par ce Z.",
  "finance.z.comptees": "Espèces comptées (comptage à l'aveugle)",
  "finance.z.comptees.hint": "Montant réellement compté dans le tiroir, sans pré-remplissage.",
  "finance.z.submit": "Geler le Z",
  "finance.z.success": "Session clôturée — Z gelé.",
  "finance.z.result.title": "Z gelé",
  "finance.z.theorique": "Total théorique",
  "finance.z.reel": "Total réel",
  "finance.z.ecart": "Écart",
  "finance.z.flagged": "Écart signalé pour revue",
  "finance.z.notFlagged": "Dans la tolérance",

  // reopen
  "finance.reopen.cta": "Rouvrir la session",
  "finance.reopen.title": "Réouverture auditée",
  "finance.reopen.warn": "La réouverture est tracée. Le Z gelé précédent est conservé dans l'audit. La fenêtre de réouverture est limitée.",
  "finance.reopen.motif": "Motif de réouverture",
  "finance.reopen.motif.hint": "Justification (médico-légal, minimum 3 caractères).",
  "finance.reopen.submit": "Rouvrir",
  "finance.reopen.success": "Session rouverte.",
  "finance.reopen.priorZ": "Z précédent (conservé)",

  // mouvements ledger
  "finance.mvt.title": "Journal des mouvements",
  "finance.mvt.subtitle": "Entrées et sorties d'espèces. Une sortie ne peut jamais dépasser le solde disponible.",
  "finance.mvt.add": "Nouveau mouvement",
  "finance.mvt.sens": "Sens",
  "finance.mvt.sens.entree": "Entrée",
  "finance.mvt.sens.sortie": "Sortie (cash-out)",
  "finance.mvt.montant": "Montant",
  "finance.mvt.motif": "Motif",
  "finance.mvt.motif.hint": "Justification (minimum 2 caractères).",
  "finance.mvt.reference": "Référence",
  "finance.mvt.reference.hint": "Pièce / bon associé (optionnel).",
  "finance.mvt.statut": "Statut",
  "finance.mvt.statut.valide": "Valide",
  "finance.mvt.statut.annule": "Annulé",
  "finance.mvt.submit": "Enregistrer le mouvement",
  "finance.mvt.success": "Mouvement enregistré.",
  "finance.mvt.cancel": "Annuler",
  "finance.mvt.cancel.title": "Annuler le mouvement",
  "finance.mvt.cancel.warn": "L'annulation est un mouvement compensatoire (jamais une suppression).",
  "finance.mvt.cancel.motif": "Motif d'annulation",
  "finance.mvt.cancel.submit": "Confirmer l'annulation",
  "finance.mvt.cancel.success": "Mouvement annulé.",
  "finance.mvt.empty.title": "Aucun mouvement",
  "finance.mvt.empty.desc": "Aucun mouvement de caisse enregistré pour cette session.",
  "finance.mvt.cashOutExceeds": "La sortie dépasse le solde de caisse disponible.",

  // corrections
  "finance.corr.title": "Corrections de caisse",
  "finance.corr.subtitle": "Remboursement ou ajustement d'une session fermée, posté sur le drawer ouvert. Le Z gelé d'origine n'est jamais modifié.",
  "finance.corr.add": "Nouvelle correction",
  "finance.corr.type": "Type",
  "finance.corr.type.refund": "Remboursement",
  "finance.corr.type.ajustement": "Ajustement",
  "finance.corr.sens": "Sens",
  "finance.corr.origine": "Session d'origine (fermée)",
  "finance.corr.origine.hint": "Identifiant de la session fermée concernée (optionnel).",
  "finance.corr.factureId": "Facture liée",
  "finance.corr.factureId.hint": "Identifiant de la facture concernée (optionnel).",
  "finance.corr.montant": "Montant",
  "finance.corr.motif": "Motif",
  "finance.corr.motif.hint": "Justification (médico-légal, minimum 3 caractères).",
  "finance.corr.submit": "Poster la correction",
  "finance.corr.success": "Correction postée.",
  "finance.corr.empty.title": "Aucune correction",
  "finance.corr.empty.desc": "Aucune correction postée sur ce drawer.",
  "finance.corr.col.type": "Type",
  "finance.corr.col.sens": "Sens",
  "finance.corr.col.montant": "Montant",
  "finance.corr.col.origine": "Origine",
  "finance.corr.col.motif": "Motif",

  // fidélité
  "finance.fid.title": "Programme de fidélité",
  "finance.fid.subtitle": "Barème de points et paliers (le % du palier EST la remise), accrual et annulation sur carte.",
  "finance.fid.tab.bareme": "Barème",
  "finance.fid.tab.carte": "Carte",
  "finance.fid.bareme.title": "Barème de fidélité",
  "finance.fid.bareme.default": "Barème par défaut (config)",
  "finance.fid.bareme.custom": "Barème personnalisé",
  "finance.fid.pointsParDinar": "Points par dinar (net payé)",
  "finance.fid.pointsParDinar.hint": "Points accumulés par DZD net réglé.",
  "finance.fid.paliers": "Paliers",
  "finance.fid.palier.code": "Code",
  "finance.fid.palier.label": "Libellé",
  "finance.fid.palier.seuil": "Seuil (points)",
  "finance.fid.palier.remise": "Remise (%)",
  "finance.fid.palier.add": "Ajouter un palier",
  "finance.fid.palier.remove": "Retirer le palier",
  "finance.fid.palier.none": "Définissez au moins un palier.",
  "finance.fid.palier.codeRequired": "Le code du palier est requis.",
  "finance.fid.bareme.save": "Enregistrer le barème",
  "finance.fid.bareme.success": "Barème enregistré.",
  "finance.fid.chart.title": "Paliers — seuil et remise",
  "finance.fid.chart.seuil": "Seuil",
  "finance.fid.chart.remise": "Remise %",

  // carte (accrual / reverse)
  "finance.fid.carte.title": "Carte de fidélité",
  "finance.fid.carte.id": "Identifiant de carte",
  "finance.fid.carte.id.hint": "Identifiant numérique de la carte.",
  "finance.fid.accrual.title": "Créditer un achat (accrual)",
  "finance.fid.accrual.netPaid": "Net payé",
  "finance.fid.accrual.netPaid.hint": "Montant net réglé pour la vente.",
  "finance.fid.accrual.factureId": "Facture",
  "finance.fid.accrual.factureId.hint": "Identifiant de la facture (optionnel).",
  "finance.fid.accrual.submit": "Créditer les points",
  "finance.fid.accrual.success": "Points crédités.",
  "finance.fid.accrual.gain": "Points gagnés",
  "finance.fid.accrual.soldeAvant": "Solde avant",
  "finance.fid.accrual.soldeApres": "Solde après",
  "finance.fid.accrual.palier": "Palier",
  "finance.fid.accrual.promoted": "Palier promu",
  "finance.fid.reverse.title": "Annuler des points",
  "finance.fid.reverse.warn": "L'annulation est un mouvement compensatoire (jamais une suppression). Le palier est recalculé à la baisse.",
  "finance.fid.reverse.points": "Points à annuler",
  "finance.fid.reverse.points.hint": "Nombre de points à retirer (entier positif).",
  "finance.fid.reverse.note": "Note",
  "finance.fid.reverse.submit": "Annuler les points",
  "finance.fid.reverse.success": "Points annulés.",
  "finance.fid.solde.title": "Solde de la carte",

  "finance.field.intInvalid": "Entier positif requis.",
  "finance.field.motifShort": "Motif trop court.",
};

const AR: Record<string, string> = {
  "nav.section.finance": "المالية",
  "nav.finance.caisse": "الصندوق",
  "nav.finance.facturation": "الفوترة",

  "finance.title": "المالية / الصندوق",
  "finance.currency": "د.ج",
  "finance.action.refresh": "تحديث",
  "finance.field.required": "حقل مطلوب.",
  "finance.field.amountInvalid": "مبلغ غير صالح.",
  "finance.amount.aria": "المبلغ بالدينار",

  "finance.caisse.title": "الصندوق",
  "finance.caisse.subtitle": "فتح، تحصيل وإغلاق جلسة الصندوق.",
  "finance.caisse.tab.session": "الجلسة",
  "finance.caisse.tab.paiement": "التحصيل",
  "finance.caisse.none.title": "لا توجد جلسة مفتوحة",
  "finance.caisse.none.desc": "افتح جلسة صندوق لبدء التحصيل.",
  "finance.caisse.open.cta": "فتح الصندوق",
  "finance.caisse.open.title": "فتح جلسة صندوق",
  "finance.caisse.open.caisseId": "معرّف الصندوق",
  "finance.caisse.open.caisseId.hint": "نقطة الصندوق (مثال CAISSE-01).",
  "finance.caisse.open.fond": "رصيد الافتتاح",
  "finance.caisse.open.fond.hint": "النقد الموجود في الدرج عند الفتح.",
  "finance.caisse.open.submit": "فتح الجلسة",
  "finance.caisse.open.success": "تم فتح جلسة الصندوق.",
  "finance.caisse.openState": "مفتوحة",
  "finance.caisse.closedState": "مغلقة",
  "finance.caisse.openedAt": "فُتحت في",
  "finance.caisse.caisseId": "الصندوق",
  "finance.caisse.fond": "رصيد الافتتاح",
  "finance.caisse.stats.title": "تحصيلات الجلسة",
  "finance.caisse.stats.total": "إجمالي التحصيل",
  "finance.caisse.stats.rembourse": "إجمالي الاسترجاع",
  "finance.caisse.stats.theorique": "النقد المتوقّع",
  "finance.caisse.close.cta": "إغلاق الصندوق",
  "finance.caisse.close.title": "إغلاق الجلسة",
  "finance.caisse.close.warn": "الإغلاق نهائي: يوقف التحصيل ويولّد تقرير Z.",
  "finance.caisse.close.comptees": "النقد المعدود",
  "finance.caisse.close.comptees.hint": "المبلغ المعدود فعليا في الدرج.",
  "finance.caisse.close.submit": "إغلاق وتوليد Z",
  "finance.caisse.close.success": "تم إغلاق الجلسة. وُلّد تقرير Z.",
  "finance.caisse.z.title": "تقرير Z",
  "finance.caisse.z.ecart": "فارق الصندوق",
  "finance.caisse.z.ecart.none": "الصندوق مطابق",
  "finance.caisse.z.ecart.surplus": "فائض",
  "finance.caisse.z.ecart.short": "نقص",

  "finance.paiement.title": "تحصيل دفعة",
  "finance.paiement.needCaisse": "يجب فتح جلسة صندوق للتحصيل.",
  "finance.paiement.factureId": "الفاتورة",
  "finance.paiement.factureId.hint": "معرّف الفاتورة المراد تسديدها.",
  "finance.paiement.montant": "المبلغ المستحق",
  "finance.paiement.montant.hint": "المبلغ المحتسب على الفاتورة.",
  "finance.paiement.tendu": "المبلغ المدفوع",
  "finance.paiement.tendu.hint": "المبلغ الذي قدّمه المريض.",
  "finance.paiement.mode": "وسيلة الدفع",
  "finance.paiement.mode.especes": "نقدا",
  "finance.paiement.mode.carte": "بطاقة",
  "finance.paiement.mode.cheque": "شيك",
  "finance.paiement.mode.autre": "أخرى",
  "finance.paiement.rendu": "الباقي المُعاد",
  "finance.paiement.tenduTooLow": "المبلغ المدفوع أقل من المبلغ المستحق.",
  "finance.paiement.submit": "تحصيل",
  "finance.paiement.success": "تم تحصيل الدفعة.",
  "finance.paiement.last": "آخر تحصيل",

  "finance.facture.title": "إصدار فاتورة",
  "finance.facture.subtitle": "أنشئ السطور، طبّق خصما، ثم أصدر.",
  "finance.facture.demandeId": "طلب التحليل",
  "finance.facture.demandeId.hint": "اختياري — يربط الفاتورة بطلب.",
  "finance.facture.lignes": "السطور",
  "finance.facture.ligne.libelle": "البيان",
  "finance.facture.ligne.prix": "السعر",
  "finance.facture.ligne.add": "إضافة سطر",
  "finance.facture.ligne.remove": "حذف السطر",
  "finance.facture.ligne.none": "أضف سطرا واحدا على الأقل.",
  "finance.facture.totalBrut": "الإجمالي الخام",
  "finance.facture.remise": "الخصم",
  "finance.facture.remise.type": "نوع الخصم",
  "finance.facture.remise.none": "بدون خصم",
  "finance.facture.remise.pourcentage": "نسبة مئوية",
  "finance.facture.remise.montant": "مبلغ ثابت",
  "finance.facture.remise.valeurPct": "النسبة (%)",
  "finance.facture.remise.valeurMontant": "مبلغ الخصم",
  "finance.facture.remise.motif": "سبب الخصم",
  "finance.facture.remise.motif.hint": "التبرير (طبي-قانوني).",
  "finance.facture.remise.pctInvalid": "النسبة بين 0 و 100.",
  "finance.facture.remise.tooBig": "الخصم يتجاوز الإجمالي الخام.",
  "finance.facture.totalNet": "الصافي المستحق",
  "finance.facture.submit": "إصدار الفاتورة",
  "finance.facture.success": "تم إصدار الفاتورة.",
  "finance.facture.issued.title": "فاتورة صادرة",
  "finance.facture.numero": "رقم الفاتورة",
  "finance.facture.payNow": "تحصيل هذه الفاتورة",

  // ── finance (06) ────────────────────────────────────────────────────────────
  "nav.finance.coffre": "الخزنة والإغلاق",
  "nav.finance.fidelite": "الولاء",

  "finance.06.sessionId": "معرّف الجلسة",
  "finance.06.sessionId.hint": "المعرّف الرقمي لجلسة الصندوق.",
  "finance.06.loadSession": "تحميل الجلسة",
  "finance.06.motif": "السبب",
  "finance.06.reference": "المرجع",
  "finance.06.optional": "اختياري",
  "finance.06.actions": "إجراءات",

  "finance.coffre.title": "الخزنة، إغلاق Z والحركات",
  "finance.coffre.subtitle": "إغلاق Z مجمّد، إعادة فتح مدققة، سجل الحركات وتصحيحات الصندوق.",
  "finance.coffre.none.title": "لا توجد جلسة مفتوحة",
  "finance.coffre.none.desc": "افتح جلسة من الصندوق، أو أدخل معرّف جلسة أدناه.",
  "finance.coffre.manualLoad": "تحميل جلسة عبر المعرّف",
  "finance.coffre.tab.xreport": "تقرير X",
  "finance.coffre.tab.mouvements": "الحركات",
  "finance.coffre.tab.corrections": "التصحيحات",
  "finance.coffre.tab.fermeture": "إغلاق Z",

  "finance.x.title": "تقرير X (لقطة)",
  "finance.x.subtitle": "صورة للصندوق دون تجميد — يُعاد حسابها عند كل قراءة.",
  "finance.x.etat": "الحالة",
  "finance.x.etat.ouverte": "مفتوحة",
  "finance.x.etat.fermee": "مغلقة",
  "finance.x.fondOuverture": "رصيد الافتتاح",
  "finance.x.totalEncaisse": "إجمالي التحصيل",
  "finance.x.totalRembourse": "إجمالي الاسترجاع",
  "finance.x.totalTheorique": "الإجمالي النظري",
  "finance.x.mouvementsCount": "عدد الحركات",
  "finance.x.frozen": "Z مجمّد",
  "finance.x.frozen.yes": "نعم",
  "finance.x.frozen.no": "لا",
  "finance.x.chart.title": "تركيبة الدرج النظري",
  "finance.x.chart.fond": "الرصيد",
  "finance.x.chart.encaisse": "محصّل",
  "finance.x.chart.rembourse": "مسترجع",
  "finance.x.chart.mouvements": "صافي الحركات",

  "finance.z.title": "إغلاق Z مجمّد",
  "finance.z.warn": "الإغلاق يجمّد Z (النظري والفعلي والفارق). كل تصحيح لاحق يمر عبر الدرج المفتوح، وليس عبر هذا Z.",
  "finance.z.comptees": "النقد المعدود (عدّ أعمى)",
  "finance.z.comptees.hint": "المبلغ المعدود فعليا في الدرج، دون ملء مسبق.",
  "finance.z.submit": "تجميد Z",
  "finance.z.success": "أُغلقت الجلسة — جُمّد Z.",
  "finance.z.result.title": "Z مجمّد",
  "finance.z.theorique": "الإجمالي النظري",
  "finance.z.reel": "الإجمالي الفعلي",
  "finance.z.ecart": "الفارق",
  "finance.z.flagged": "فارق مُعلَّم للمراجعة",
  "finance.z.notFlagged": "ضمن التفاوت المسموح",

  "finance.reopen.cta": "إعادة فتح الجلسة",
  "finance.reopen.title": "إعادة فتح مدققة",
  "finance.reopen.warn": "إعادة الفتح مُسجَّلة. يُحفظ Z المجمّد السابق في التدقيق. نافذة إعادة الفتح محدودة.",
  "finance.reopen.motif": "سبب إعادة الفتح",
  "finance.reopen.motif.hint": "تبرير (طبي-قانوني، 3 أحرف على الأقل).",
  "finance.reopen.submit": "إعادة الفتح",
  "finance.reopen.success": "أُعيد فتح الجلسة.",
  "finance.reopen.priorZ": "Z السابق (محفوظ)",

  "finance.mvt.title": "سجل الحركات",
  "finance.mvt.subtitle": "إدخالات وإخراجات نقدية. لا يمكن للإخراج أن يتجاوز الرصيد المتاح.",
  "finance.mvt.add": "حركة جديدة",
  "finance.mvt.sens": "الاتجاه",
  "finance.mvt.sens.entree": "إدخال",
  "finance.mvt.sens.sortie": "إخراج (نقد)",
  "finance.mvt.montant": "المبلغ",
  "finance.mvt.motif": "السبب",
  "finance.mvt.motif.hint": "تبرير (حرفان على الأقل).",
  "finance.mvt.reference": "المرجع",
  "finance.mvt.reference.hint": "مستند / سند مرتبط (اختياري).",
  "finance.mvt.statut": "الحالة",
  "finance.mvt.statut.valide": "صالح",
  "finance.mvt.statut.annule": "ملغى",
  "finance.mvt.submit": "تسجيل الحركة",
  "finance.mvt.success": "سُجّلت الحركة.",
  "finance.mvt.cancel": "إلغاء",
  "finance.mvt.cancel.title": "إلغاء الحركة",
  "finance.mvt.cancel.warn": "الإلغاء حركة تعويضية (وليس حذفا).",
  "finance.mvt.cancel.motif": "سبب الإلغاء",
  "finance.mvt.cancel.submit": "تأكيد الإلغاء",
  "finance.mvt.cancel.success": "أُلغيت الحركة.",
  "finance.mvt.empty.title": "لا توجد حركات",
  "finance.mvt.empty.desc": "لا توجد حركات صندوق مسجلة لهذه الجلسة.",
  "finance.mvt.cashOutExceeds": "الإخراج يتجاوز رصيد الصندوق المتاح.",

  "finance.corr.title": "تصحيحات الصندوق",
  "finance.corr.subtitle": "استرجاع أو تعديل لجلسة مغلقة، يُسجَّل على الدرج المفتوح. لا يُعدَّل Z المجمّد الأصلي أبدا.",
  "finance.corr.add": "تصحيح جديد",
  "finance.corr.type": "النوع",
  "finance.corr.type.refund": "استرجاع",
  "finance.corr.type.ajustement": "تعديل",
  "finance.corr.sens": "الاتجاه",
  "finance.corr.origine": "الجلسة الأصلية (مغلقة)",
  "finance.corr.origine.hint": "معرّف الجلسة المغلقة المعنية (اختياري).",
  "finance.corr.factureId": "الفاتورة المرتبطة",
  "finance.corr.factureId.hint": "معرّف الفاتورة المعنية (اختياري).",
  "finance.corr.montant": "المبلغ",
  "finance.corr.motif": "السبب",
  "finance.corr.motif.hint": "تبرير (طبي-قانوني، 3 أحرف على الأقل).",
  "finance.corr.submit": "تسجيل التصحيح",
  "finance.corr.success": "سُجّل التصحيح.",
  "finance.corr.empty.title": "لا توجد تصحيحات",
  "finance.corr.empty.desc": "لا توجد تصحيحات مسجلة على هذا الدرج.",
  "finance.corr.col.type": "النوع",
  "finance.corr.col.sens": "الاتجاه",
  "finance.corr.col.montant": "المبلغ",
  "finance.corr.col.origine": "الأصل",
  "finance.corr.col.motif": "السبب",

  "finance.fid.title": "برنامج الولاء",
  "finance.fid.subtitle": "سُلّم النقاط والمستويات (نسبة المستوى هي الخصم)، الإضافة والإلغاء على البطاقة.",
  "finance.fid.tab.bareme": "السُّلّم",
  "finance.fid.tab.carte": "البطاقة",
  "finance.fid.bareme.title": "سُلّم الولاء",
  "finance.fid.bareme.default": "السُّلّم الافتراضي (الإعداد)",
  "finance.fid.bareme.custom": "سُلّم مخصّص",
  "finance.fid.pointsParDinar": "نقاط لكل دينار (صافي مدفوع)",
  "finance.fid.pointsParDinar.hint": "النقاط المتراكمة لكل دينار صافٍ مُسدَّد.",
  "finance.fid.paliers": "المستويات",
  "finance.fid.palier.code": "الرمز",
  "finance.fid.palier.label": "التسمية",
  "finance.fid.palier.seuil": "العتبة (نقاط)",
  "finance.fid.palier.remise": "الخصم (%)",
  "finance.fid.palier.add": "إضافة مستوى",
  "finance.fid.palier.remove": "حذف المستوى",
  "finance.fid.palier.none": "حدّد مستوى واحدا على الأقل.",
  "finance.fid.palier.codeRequired": "رمز المستوى مطلوب.",
  "finance.fid.bareme.save": "حفظ السُّلّم",
  "finance.fid.bareme.success": "حُفظ السُّلّم.",
  "finance.fid.chart.title": "المستويات — العتبة والخصم",
  "finance.fid.chart.seuil": "العتبة",
  "finance.fid.chart.remise": "خصم %",

  "finance.fid.carte.title": "بطاقة الولاء",
  "finance.fid.carte.id": "معرّف البطاقة",
  "finance.fid.carte.id.hint": "المعرّف الرقمي للبطاقة.",
  "finance.fid.accrual.title": "إضافة رصيد لعملية شراء",
  "finance.fid.accrual.netPaid": "الصافي المدفوع",
  "finance.fid.accrual.netPaid.hint": "المبلغ الصافي المُسدَّد للبيع.",
  "finance.fid.accrual.factureId": "الفاتورة",
  "finance.fid.accrual.factureId.hint": "معرّف الفاتورة (اختياري).",
  "finance.fid.accrual.submit": "إضافة النقاط",
  "finance.fid.accrual.success": "أُضيفت النقاط.",
  "finance.fid.accrual.gain": "النقاط المكتسبة",
  "finance.fid.accrual.soldeAvant": "الرصيد قبل",
  "finance.fid.accrual.soldeApres": "الرصيد بعد",
  "finance.fid.accrual.palier": "المستوى",
  "finance.fid.accrual.promoted": "تمت الترقية",
  "finance.fid.reverse.title": "إلغاء نقاط",
  "finance.fid.reverse.warn": "الإلغاء حركة تعويضية (وليس حذفا). يُعاد حساب المستوى نزولا.",
  "finance.fid.reverse.points": "نقاط للإلغاء",
  "finance.fid.reverse.points.hint": "عدد النقاط المراد إزالتها (عدد صحيح موجب).",
  "finance.fid.reverse.note": "ملاحظة",
  "finance.fid.reverse.submit": "إلغاء النقاط",
  "finance.fid.reverse.success": "أُلغيت النقاط.",
  "finance.fid.solde.title": "رصيد البطاقة",

  "finance.field.intInvalid": "عدد صحيح موجب مطلوب.",
  "finance.field.motifShort": "السبب قصير جدا.",
};

let registered = false;

/** Idempotent — registers the finance namespace into both dictionaries exactly once. */
export function registerFinanceMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
